import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Snackbar,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import { TechRadarComponent } from '@backstage-community/plugin-tech-radar';
import { Page, Content, MarkdownContent } from '@backstage/core-components';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { bumpTechRadarSeq } from '../../apis';

/** Shape of a DB row */
interface TechRadarItem {
  id: number;
  area: string;
  category: string;
  stage: string;
  technology: string;
  open_source_commercial: string;
  recommendation: string;
  costs: string;
  risks: string;
}

type SortableField = 'area' | 'category' | 'stage' | 'technology' | 'open_source_commercial';
type SortDirection = 'asc' | 'desc';

/** Normalise legacy stage names to current display labels */
function normalizeStageLabel(stage: string): string {
  if (!stage) return stage;
  if (stage.toLowerCase() === 'adopt') return 'Provisioned';
  return stage;
}

const STAGE_COLORS_SIMPLE: Record<string, string> = {
  Provisioned: '#81c784',
  Assess: '#ffd54f',
  Deprecated: '#e57373',
};

const CATEGORY_COLORS: Record<string, string> = {
  Platform: '#82b1ff',
  Platforms: '#82b1ff',
  Tools: '#80cbc4',
  'Shared Services': '#ce93d8',
  Techniques: '#ffcc80',
};

const STAGES_SIMPLE_LIST = [
  { label: 'Provisioned', color: '#81c784' },
  { label: 'Assess', color: '#ffd54f' },
  { label: 'Deprecated', color: '#e57373' },
];

interface TocEntry { level: number; text: string; id: string; }

const EMOJI_RE = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu;

function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(EMOJI_RE, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function stripEmoji(s: string): string {
  return s.replace(EMOJI_RE, '').trim();
}

function normalizeFilterValue(s: string): string {
  return (s ?? '').trim().toLowerCase();
}

function parseToc(md: string): TocEntry[] {
  const entries: TocEntry[] = [];
  for (const line of md.split('\n')) {
    const match = line.match(/^(#{1,3})\s+(.+)$/);
    if (match) {
      const raw = match[2].replace(/[*_`]/g, '');
      const text = stripEmoji(raw);
      const id = toSlug(raw);
      entries.push({ level: match[1].length, text, id });
    }
  }
  return entries;
}

const NAV_WIDTH = 260;
const TECH_RADAR_REPO_WEB_URL = 'https://github.com/verint-Architecture/tech-radar';

/** Module-level cache so re-navigating to Tech Radar skips re-fetching */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
interface TechRadarCache {
  versions: Array<{ id: string; label: string }>;
  areas: string[];
  items: TechRadarItem[];
  selectedVersion: string;
  fetchedAt: number;
}
let _techRadarCache: TechRadarCache | null = null;

function getCachedData(): TechRadarCache | null {
  if (!_techRadarCache) return null;
  if (Date.now() - _techRadarCache.fetchedAt > CACHE_TTL_MS) {
    _techRadarCache = null;
    return null;
  }
  return _techRadarCache;
}

/** Exposed for tests only — clears the module-level cache between test runs */
export function resetTechRadarCache() {
  _techRadarCache = null;
}

const useStyles = makeStyles(theme => ({
  guideWrapper: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  guideNav: {
    width: NAV_WIDTH,
    minWidth: NAV_WIDTH,
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 220px)',
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(1),
  },
  guideNavItem: {
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
    },
  },
  guideNavItemActive: {
    background: theme.palette.type === 'dark' ? 'rgba(144,202,249,0.15)' : 'rgba(25,118,210,0.08)',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  guideNavText: {
    '& span': {
      fontSize: '0.8125rem',
      lineHeight: 1.4,
    },
  },
  guideContent: {
    flex: 1,
    minWidth: 0,
    maxWidth: 900,
    '& h1': {
      fontSize: '2rem',
      fontWeight: 400,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(1),
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 400,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(0.5),
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
    },
    '& h3': {
      fontSize: '1.25rem',
      fontWeight: 500,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1),
    },
    '& p': {
      lineHeight: 1.7,
      marginBottom: theme.spacing(1.5),
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(1, 2),
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      '& p': { margin: 0 },
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
      '& li': { lineHeight: 1.7, marginBottom: theme.spacing(0.5) },
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(3, 0),
    },
    '& strong': {
      fontWeight: 600,
    },
    '& code': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      padding: '2px 6px',
      borderRadius: 3,
      fontSize: '0.875em',
    },
  },
}));

export const TechRadarWrapper = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const HEADER_STYLE: React.CSSProperties = { color: isDark ? 'rgba(255,255,255,0.7)' : undefined, fontWeight: 700 };
  const navigate = useNavigate();
  const discoveryApi = useApi(discoveryApiRef);

  const _cached = getCachedData();
  const [items, setItems] = useState<TechRadarItem[]>(_cached?.items ?? []);
  const [snackMsg, setSnackMsg] = useState('');
  const [searchText, setSearchText] = useState('');
  const [hoveredQuadrant, setHoveredQuadrant] = useState<string | null>(null);
  const [hoveredRadarQuadrant, setHoveredRadarQuadrant] = useState<string | null>(null);
  const radarRef = React.useRef<HTMLDivElement>(null);
  const radarContainerRef = React.useRef<HTMLDivElement>(null);
  const [radarWidth, setRadarWidth] = useState(1200);
  const [filterArea, setFilterArea] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortField, setSortField] = useState<SortableField>('area');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // Blip stats (votes + comment counts) for tooltips and dialogs
  interface BlipStatEntry { up: number; down: number; commentCount: number; }
  const [blipStats, setBlipStats] = useState<Record<string, BlipStatEntry>>({});
  const blipStatsRef = useRef<Record<string, BlipStatEntry>>({});
  useEffect(() => { blipStatsRef.current = blipStats; }, [blipStats]);

  // Quick-view dialog for table rows
  const [blipDialogItem, setBlipDialogItem] = useState<TechRadarItem | null>(null);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [radarSeq, setRadarSeq] = useState(0); // bumped after re-seed to force radar re-fetch
  const [versions, setVersions] = useState<Array<{ id: string; label: string }>>(_cached?.versions ?? []);
  const [areaOptions, setAreaOptions] = useState<string[]>(_cached?.areas ?? []);
  const [selectedVersion, setSelectedVersion] = useState<string>(_cached?.selectedVersion ?? '');
  const [activeTab, setActiveTab] = useState(0);

  // Guide/Breakdown tab state
  const [guideMarkdown, setGuideMarkdown] = useState<string | null>(null);
  const [guideError, setGuideError] = useState(false);
  const [guideActiveId, setGuideActiveId] = useState<string>('');
  const guideContentRef = useRef<HTMLDivElement>(null);

  const guideToc = useMemo(() => (guideMarkdown ? parseToc(guideMarkdown) : []), [guideMarkdown]);

  // When mouse moves over the radar SVG, find which legend panel corner matches
  // the mouse direction from centre and highlight that quadrant's h2 heading.
  const handleRadarMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!radarRef.current) return;
    const svg = radarRef.current.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    // Only react when inside the circular radar area
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 30 || dist > Math.min(rect.width, rect.height) / 2) {
      setHoveredRadarQuadrant(null);
      return;
    }
    // Find the foreignObject whose corner direction matches the mouse direction
    const fos = Array.from(radarRef.current.querySelectorAll('[data-testid="radar-quadrant"]'));
    for (const fo of fos) {
      const fr = fo.getBoundingClientRect();
      const foDx = (fr.left + fr.right) / 2 - (rect.left + rect.width / 2);
      const foDy = (fr.top + fr.bottom) / 2 - (rect.top + rect.height / 2);
      if (Math.sign(dx) === Math.sign(foDx) && Math.sign(dy) === Math.sign(foDy)) {
        const h2 = fo.querySelector('h2');
        setHoveredRadarQuadrant(h2?.textContent?.trim() ?? null);
        return;
      }
    }
    setHoveredRadarQuadrant(null);
  }, []);

  // Measure container width so the radar SVG fills it without horizontal scroll
  useEffect(() => {
    const el = radarContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        if (w > 0) setRadarWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!radarRef.current) return;
    radarRef.current.querySelectorAll('[data-testid="radar-quadrant"]').forEach(fo => {
      const h2 = fo.querySelector('h2') as HTMLElement | null;
      const panel = fo.querySelector('div') as HTMLElement | null;
      const isActive = h2?.textContent?.trim() === hoveredRadarQuadrant;
      if (h2) {
        h2.style.color = isActive ? '#42a5f5' : '';
        h2.style.fontWeight = isActive ? '900' : '';
        h2.style.textShadow = isActive ? '0 0 12px rgba(66,165,245,0.45)' : '';
      }
      if (panel) {
        panel.style.background = isActive
          ? 'linear-gradient(135deg, rgba(144,202,249,0.18) 0%, rgba(41,182,246,0.13) 60%, rgba(100,181,246,0.08) 100%)'
          : '';
        panel.style.boxShadow = isActive ? 'inset 0 1px 0 rgba(255,255,255,0.15), 0 0 24px rgba(41,182,246,0.18)' : '';
        panel.style.border = isActive ? '1px solid rgba(41,182,246,0.4)' : '';
      }
    });
  }, [hoveredRadarQuadrant]);

  const getBaseUrl = useCallback(async () => {
    return discoveryApi.getBaseUrl('tech-radar');
  }, [discoveryApi]);

  // Fetch bulk blip stats (votes + comment counts) for tooltips
  const fetchBlipStats = useCallback(async () => {
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetch(`${base}/blips/stats`);
      if (resp.ok) {
        const { stats } = await resp.json();
        setBlipStats(stats ?? {});
      }
    } catch { /* non-fatal */ }
  }, [discoveryApi]);

  // Attach hover tooltips to radar quadrant panel list items showing vote+comment stats
  useEffect(() => {
    const container = radarRef.current;
    if (!container || activeTab !== 0) return;
    let tooltipEl: HTMLDivElement | null = null;
    const handlers: Array<{ el: Element; enter: EventListener; leave: EventListener }> = [];

    const hide = () => { if (tooltipEl) { tooltipEl.remove(); tooltipEl = null; } };

    const show = (anchor: HTMLAnchorElement) => {
      hide();
      const href = anchor.getAttribute('href') ?? '';
      const slug = href.split('/blips/').pop();
      if (!slug) return;
      const s = blipStatsRef.current[slug];
      if (!s || (s.up === 0 && s.down === 0 && s.commentCount === 0)) return;
      tooltipEl = document.createElement('div');
      tooltipEl.style.cssText = 'position:fixed;z-index:9999;background:rgba(20,20,20,0.95);border:1px solid rgba(255,255,255,0.15);border-radius:6px;padding:5px 10px;font-size:11px;color:#fff;pointer-events:none;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.5)';
      tooltipEl.textContent = `👍 ${s.up}  👎 ${s.down}  💬 ${s.commentCount}`;
      document.body.appendChild(tooltipEl);
      const rect = anchor.getBoundingClientRect();
      tooltipEl.style.left = `${rect.left}px`;
      tooltipEl.style.top = `${rect.bottom + 4}px`;
    };

    const attach = () => {
      handlers.forEach(({ el, enter, leave }) => { el.removeEventListener('mouseenter', enter); el.removeEventListener('mouseleave', leave); });
      handlers.length = 0;
      container.querySelectorAll('[data-testid="radar-quadrant"] a').forEach(a => {
        const enter: EventListener = () => show(a as HTMLAnchorElement);
        const leave: EventListener = hide;
        a.addEventListener('mouseenter', enter);
        a.addEventListener('mouseleave', leave);
        handlers.push({ el: a, enter, leave });
      });
    };

    const observer = new MutationObserver(attach);
    observer.observe(container, { childList: true, subtree: true });
    attach();

    return () => {
      observer.disconnect();
      hide();
      handlers.forEach(({ el, enter, leave }) => { el.removeEventListener('mouseenter', enter); el.removeEventListener('mouseleave', leave); });
    };
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available versions from manifest files
  const fetchVersions = useCallback(async () => {
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetch(`${base}/versions`);
      const { versions: list } = await resp.json();
      const loaded: Array<{ id: string; label: string }> = list ?? [];
      setVersions(loaded);
      _techRadarCache = { ..._techRadarCache ?? { items: [], areas: [], selectedVersion: '', fetchedAt: 0 }, versions: loaded, fetchedAt: Date.now() };
    } catch { /* non-fatal */ }
  }, [discoveryApi]);

  // Fetch canonical area options from areas.yaml via backend endpoint
  const fetchAreas = useCallback(async () => {
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetch(`${base}/areas`);
      if (!resp.ok) return;
      const payload = await resp.json();
      const names = Array.isArray(payload?.areas)
        ? payload.areas
            .map((a: any) => (typeof a === 'string' ? a : a?.name))
            .filter((a: any) => typeof a === 'string' && a.trim().length > 0)
        : [];
      setAreaOptions(names);
      _techRadarCache = { ..._techRadarCache ?? { items: [], versions: [], selectedVersion: '', fetchedAt: 0 }, areas: names, fetchedAt: Date.now() };
    } catch {
      // Non-fatal: fallback to deriving areas from loaded items.
    }
  }, [discoveryApi]);

  // Fetch items for the table (version file when provided, otherwise DB fallback)
  const fetchItems = useCallback(async (version?: string) => {
    try {
      const base = await getBaseUrl();
      const url = version
        ? `${base}/versions/${encodeURIComponent(version)}`
        : `${base}/items`;
      const resp = await fetch(url);
      const { items: list } = await resp.json();
      const loaded = list ?? [];
      setItems(loaded);
      _techRadarCache = { ..._techRadarCache ?? { versions: [], areas: [], fetchedAt: 0 }, items: loaded, selectedVersion: version ?? '', fetchedAt: Date.now() };
    } catch {
      setSnackMsg('Failed to load tech radar data');
    }
  }, [getBaseUrl]);

  // Restore tab + filters saved before navigating to a blip page
  useEffect(() => {
    const saved = sessionStorage.getItem('techRadarState');
    if (saved) {
      try {
        const s = JSON.parse(saved);
        if (s.tab !== undefined) setActiveTab(s.tab);
        if (s.filterArea !== undefined) setFilterArea(s.filterArea);
        if (s.filterStage !== undefined) setFilterStage(s.filterStage);
        if (s.filterCategory !== undefined) setFilterCategory(s.filterCategory);
      } catch { /* ignore corrupt data */ }
      sessionStorage.removeItem('techRadarState');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Skip fetching if we already seeded from cache
    if (getCachedData()) return;
    fetchVersions();
    fetchAreas();
    fetchBlipStats();
  }, [fetchAreas, fetchVersions, fetchBlipStats]);

  useEffect(() => {
    // Skip fetching if we already seeded from cache
    if (getCachedData() && items.length > 0) return;
    if (versions.length > 0 && !selectedVersion) {
      const newest = versions[0].id;
      setSelectedVersion(newest);
      fetchItems(newest);
      return;
    }
    if (versions.length === 0 && !selectedVersion) {
      fetchItems();
    }
  }, [versions, selectedVersion, fetchItems, items.length]);

  const handleVersionChange = (v: string) => {    setSelectedVersion(v);
    fetchItems(v || undefined);
    // Radar re-fetches automatically because id prop changes (useAsync dep)
  };

  // Fetch guide markdown for the Break Down tab
  const fetchGuide = useCallback(async () => {
    try {
      const base = await getBaseUrl();
      const resp = await fetch(`${base}/guide`);
      if (!resp.ok) {
        setGuideError(true);
        return;
      }
      setGuideMarkdown(await resp.text());
    } catch {
      setGuideError(true);
    }
  }, [getBaseUrl]);

  useEffect(() => {
    if (activeTab === 2 && !guideMarkdown && !guideError) {
      fetchGuide();
    }
  }, [activeTab, guideMarkdown, guideError, fetchGuide]);

  // Re-fetch table items whenever the user switches to the Table View tab.
  // The TechRadarComponent remounts on every Tab 0 visit (conditional render),
  // but fetchItems() only runs once on mount — this keeps the table in sync.
  useEffect(() => {
    if (activeTab !== 1) return;
    fetchItems(selectedVersion || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Inject heading IDs for guide TOC scroll-to
  const injectGuideHeadingIds = useCallback(() => {
    const container = guideContentRef.current;
    if (!container || !guideToc.length) return;
    const headings = container.querySelectorAll('h1, h2, h3');
    const tocCopy = [...guideToc];
    headings.forEach(el => {
      const slug = toSlug(el.textContent || '');
      const idx = tocCopy.findIndex(t => t.id === slug);
      if (idx !== -1) {
        el.setAttribute('id', slug);
        tocCopy.splice(idx, 1);
      }
    });
  }, [guideToc]);

  useEffect(() => {
    if (activeTab !== 2 || !guideToc.length) return;
    const timer = setTimeout(() => {
      injectGuideHeadingIds();
      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setGuideActiveId(entry.target.id);
              break;
            }
          }
        },
        { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 },
      );
      for (const item of guideToc) {
        const el = document.getElementById(item.id);
        if (el) observer.observe(el);
      }
      return () => observer.disconnect();
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab, guideToc, injectGuideHeadingIds]);

  const scrollToGuideSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setGuideActiveId(id);
    }
  };

  // Navigate to a blip page, saving current tab + filters so they can be restored on back
  const navigateToBlip = (slug: string) => {
    sessionStorage.setItem('techRadarState', JSON.stringify({ tab: activeTab, filterArea, filterStage, filterCategory }));
    const tabLabel = activeTab === 0 ? 'Tech Landscape' : activeTab === 1 ? 'Table View' : 'Break Down';
    navigate(`/tech-radar/blip/${slug}`, { state: { fromTabLabel: tabLabel } });
  };

  // Filtered + sorted items
  const uniqueAreas = useMemo(() => {
    if (areaOptions.length > 0) return areaOptions;
    return Array.from(new Set(items.map(i => i.area).filter(Boolean))).sort();
  }, [areaOptions, items]);
  const uniqueCategories = useMemo(() => Array.from(new Set(items.map(i => i.category).filter(Boolean))).sort(), [items]);
  const stagesList = STAGES_SIMPLE_LIST;
  const stageColors = STAGE_COLORS_SIMPLE;

  const displayItems = useMemo(() => {
    const lowerSearch = searchText.toLowerCase();
    let filtered = items;
    if (lowerSearch) {
      filtered = filtered.filter(item =>
        (item.technology ?? '').toLowerCase().includes(lowerSearch) ||
        (item.area ?? '').toLowerCase().includes(lowerSearch) ||
        (item.category ?? '').toLowerCase().includes(lowerSearch) ||
        (item.stage ? normalizeStageLabel(item.stage) : '').toLowerCase().includes(lowerSearch) ||
        (item.open_source_commercial ?? '').toLowerCase().includes(lowerSearch) ||
        (item.recommendation ?? '').toLowerCase().includes(lowerSearch),
      );
    }
    if (filterArea) {
      const selectedArea = normalizeFilterValue(filterArea);
      filtered = filtered.filter(i => normalizeFilterValue(i.area) === selectedArea);
    }
    if (filterStage) filtered = filtered.filter(i => normalizeStageLabel(i.stage) === filterStage);
    if (filterCategory) filtered = filtered.filter(i => i.category === filterCategory);
    return [...filtered].sort((a, b) => {
      const aVal = (a[sortField] ?? '').toLowerCase();
      const bVal = (b[sortField] ?? '').toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [items, searchText, filterArea, filterStage, filterCategory, sortField, sortDir]);

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Re-seed DB from techradar_latest.md — clears and re-seeds
  const reseed = async () => {
    try {
      const base = await getBaseUrl();
      const resp = await fetch(`${base}/seed`, { method: 'POST' });
      if (!resp.ok) throw new Error();
      const { message } = await resp.json();
      // fetchVersions will auto-select the newest version
      await fetchVersions();
      await fetchItems();
      bumpTechRadarSeq(); // module-level bump so load() sees a new id
      setRadarSeq(s => s + 1); // changes id prop → radar re-fetches
      setSnackMsg(message ?? 'Re-seeded from techradar_latest.md');
    } catch {
      setSnackMsg('Failed to re-seed');
    }
  };

  const openDownloadDialog = () => setIsDownloadDialogOpen(true);

  const sortLabel = (field: SortableField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDir : 'asc'}
      onClick={() => handleSort(field)}
      style={{ color: isDark ? 'rgba(255,255,255,0.7)' : undefined }}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Page themeId="tool">
      <Content>
        <Box style={{ background: isDark ? '#111' : undefined, margin: '-24px', padding: '24px', minHeight: 'calc(100vh - 150px)' }}>
        {/* Tabs + Version selector on same line */}
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 24 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Tech Landscape" />
            <Tab label="Table View" />
            <Tab label="Break Down" />
          </Tabs>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.55)' : undefined }}>Version:</Typography>
            <Select
              value={selectedVersion}
              onChange={e => handleVersionChange(e.target.value as string)}
              displayEmpty
              variant="outlined"
              style={{ color: isDark ? '#fff' : undefined, minWidth: 220, height: 36, fontSize: 13 }}
            >
              {versions.length === 0 && <MenuItem value="">Latest</MenuItem>}
              {versions.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
              ))}
            </Select>
            <Tooltip title="Re-seed from GitHub repo">
              <Button
                size="small"
                variant="outlined"
                onClick={reseed}
                style={{ height: 36, color: isDark ? 'rgba(255,255,255,0.7)' : undefined, borderColor: isDark ? 'rgba(255,255,255,0.3)' : undefined, whiteSpace: 'nowrap' }}
              >
                Reseed
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Tab 1: Radar Diagram */}
        <div style={{ display: activeTab === 0 ? undefined : 'none' }}>
            {/* Legend bar */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              mb={3}
              p={2}
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: 8,
                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                gap: 12,
              }}
            >
              <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)', alignSelf: 'center', marginRight: 4 }}>Quadrants:</Typography>
                {[
                  { label: 'Platforms', color: '#82b1ff' },
                  { label: 'Tools', color: '#80cbc4' },
                  { label: 'Shared Services', color: '#ce93d8' },
                  { label: 'Techniques', color: '#ffcc80' },
                ].map(q => (
                  <span
                    key={q.label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 24,
                      padding: '0 8px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600,
                      background: q.color,
                      color: '#000',
                      userSelect: 'none',
                    }}
                  >
                    {q.label}
                  </span>
                ))}
                <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)', alignSelf: 'center', marginLeft: 8, marginRight: 4 }}>Stages:</Typography>
                {stagesList.map(s => (
                  <Tooltip key={s.label} title={s.label}>
                    <Chip label={s.label} size="small" style={{ background: s.color, color: '#000', fontWeight: 600, fontSize: 11 }} />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            <style>{`
              [data-testid="radar-ring-heading"] {
                paint-order: stroke fill;
                stroke: #1a2a1a;
                stroke-width: 12px;
                stroke-linejoin: round;
                font-weight: bold;
                font-size: 18px !important;
                filter: drop-shadow(0 0 6px rgba(26,42,26,0.9));
              }
              [data-testid="radar-quadrant"] h2 {
                transition: color 0.15s, font-weight 0.1s;
              }
              [data-testid="radar-quadrant"]:hover h2 {
                color: #42a5f5 !important;
                font-weight: 900 !important;
                text-shadow: 0 0 12px rgba(66,165,245,0.4);
              }
              foreignObject[data-testid="radar-quadrant"] {
                width: 20% !important;
              }
              foreignObject[data-testid="radar-quadrant"]:nth-of-type(1) {
                x: 80% !important;
              }
              foreignObject[data-testid="radar-quadrant"]:nth-of-type(2) {
                x: 0% !important;
              }
              foreignObject[data-testid="radar-quadrant"]:nth-of-type(3) {
                x: 0% !important;
              }
              foreignObject[data-testid="radar-quadrant"]:nth-of-type(4) {
                x: 80% !important;
              }
              [data-testid="radar-quadrant"] > div {
                padding: 4px 8px;
                border-radius: 14px;
                border: 1px solid transparent;
                transition: background 0.2s, box-shadow 0.2s, border-color 0.2s;
              }
              [data-testid="radar-quadrant"] > div:hover {
                background: linear-gradient(135deg, rgba(144,202,249,0.18) 0%, rgba(41,182,246,0.13) 60%, rgba(100,181,246,0.08) 100%) !important;
                box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), 0 0 24px rgba(41,182,246,0.18) !important;
                border: 1px solid rgba(41,182,246,0.4) !important;
              }
              [data-testid="radar-quadrant"] li {
                margin: 0 !important;
                padding: 0 !important;
                line-height: 1.3 !important;
                font-size: 11px !important;
              }
              [data-testid="radar-quadrant"] h2 {
                margin: 2px 0 !important;
                font-size: 14px !important;
              }
              [data-testid="radar-quadrant"] h3 {
                margin: 4px 0 2px !important;
                font-size: 12px !important;
              }
              [data-testid="radar-quadrant"] ul {
                margin: 0 !important;
                padding-left: 8px !important;
              }
              [data-testid="radar-quadrant"] li,
              [data-testid="radar-quadrant"] a {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: block;
                max-width: 100%;
              }
            `}</style>
            <div
              ref={radarContainerRef}
              style={{ marginBottom: 32, position: 'relative' }}
            >
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 0,
                userSelect: 'none',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                  <text
                    x="420"
                    y="680"
                    textAnchor="middle"
                    fontFamily="Arial, Helvetica, sans-serif"
                    fontWeight="900"
                    fontSize="700"
                    fill="rgba(66,133,244,0.12)"
                    letterSpacing="0.05em"
                  >V</text>
                </svg>
              </div>
              <div
                ref={radarRef}
                onMouseMove={handleRadarMouseMove}
                onMouseLeave={() => setHoveredRadarQuadrant(null)}
                style={{ position: 'relative', zIndex: 1 }}
              >
                <TechRadarComponent id={`${selectedVersion}:${radarSeq}`} width={radarWidth} height={700} />
              </div>
            </div>
        </div>

        {/* Tab 2: Table View */}
        <div style={{ display: activeTab === 1 ? undefined : 'none' }}>
            {/* Quadrant filter bar */}
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              mb={3}
              p={2}
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: 8,
                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                gap: 12,
              }}
            >
              <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)', alignSelf: 'center', marginRight: 4 }}>Quadrants:</Typography>
                {[
                  { label: 'Platforms', value: 'Platform', color: '#82b1ff' },
                  { label: 'Tools', value: 'Tools', color: '#80cbc4' },
                  { label: 'Shared Services', value: 'Shared Services', color: '#ce93d8' },
                  { label: 'Techniques', value: 'Techniques', color: '#ffcc80' },
                ].map(q => {
                  const isHovered = hoveredQuadrant === q.label;
                  const isActive = filterCategory === q.value;
                  const highlight = isHovered || isActive;
                  return (
                    <span
                      key={q.label}
                      role="button"
                      tabIndex={0}
                      onClick={() => setFilterCategory(isActive ? '' : q.value)}
                      onMouseEnter={() => setHoveredQuadrant(q.label)}
                      onMouseLeave={() => setHoveredQuadrant(null)}
                      onKeyDown={e => e.key === 'Enter' && setFilterCategory(isActive ? '' : q.value)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 24,
                        padding: '0 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: highlight ? 700 : 600,
                        background: highlight ? '#1565c0' : q.color,
                        color: highlight ? '#fff' : '#000',
                        boxShadow: highlight ? '0 0 0 2px #90caf9' : 'none',
                        cursor: 'pointer',
                        transition: 'background 0.15s, color 0.15s, box-shadow 0.15s, font-weight 0.1s',
                        userSelect: 'none',
                        outline: 'none',
                      }}
                    >
                      {q.label}
                    </span>
                  );
                })}
                <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)', alignSelf: 'center', marginLeft: 8, marginRight: 4 }}>Stages:</Typography>
                {stagesList.map(s => {
                  const isActive = filterStage === s.label;
                  return (
                    <span
                      key={s.label}
                      role="button"
                      tabIndex={0}
                      onClick={() => setFilterStage(isActive ? '' : s.label)}
                      onKeyDown={e => e.key === 'Enter' && setFilterStage(isActive ? '' : s.label)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 24,
                        padding: '0 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        background: isActive ? '#333' : s.color,
                        color: isActive ? '#fff' : '#000',
                        boxShadow: isActive ? `0 0 0 2px ${s.color}` : 'none',
                        cursor: 'pointer',
                        transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
                        userSelect: 'none',
                        outline: 'none',
                      }}
                    >
                      {s.label}
                    </span>
                  );
                })}
              </Box>
            </Box>

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={openDownloadDialog}
                  style={{ color: '#90caf9', borderColor: '#90caf9' }}
                >
                  GIT Repo
                </Button>
              </Box>
            </Box>

            {/* Search + Filters */}
            <Box display="flex" alignItems="center" mb={2} style={{ gap: 8, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search technologies..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                variant="outlined"
                size="small"
                style={{ flex: '1 1 220px', minWidth: 180 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: isDark ? 'rgba(255,255,255,0.4)' : undefined }} />
                    </InputAdornment>
                  ),
                  style: { color: isDark ? '#fff' : undefined, background: isDark ? 'rgba(255,255,255,0.06)' : undefined, borderRadius: 8 },
                }}
              />
              <Select
                value={filterArea}
                onChange={e => setFilterArea(e.target.value as string)}
                variant="outlined"
                displayEmpty
                style={{ color: isDark ? (filterArea ? '#fff' : 'rgba(255,255,255,0.4)') : undefined, height: 40, minWidth: 160, fontSize: 13 }}
              >
                <MenuItem value="">All Areas</MenuItem>
                {uniqueAreas.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
              <Select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value as string)}
                variant="outlined"
                displayEmpty
                style={{ color: isDark ? (filterCategory ? '#fff' : 'rgba(255,255,255,0.4)') : undefined, height: 40, minWidth: 180, fontSize: 13 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {uniqueCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
              {(filterArea || filterStage || filterCategory) && (
                <Button
                  size="small"
                  onClick={() => { setFilterArea(''); setFilterStage(''); setFilterCategory(''); }}
                  style={{ color: isDark ? 'rgba(255,255,255,0.5)' : undefined, fontSize: 12, minWidth: 'unset' }}
                >
                  Clear filters
                </Button>
              )}
            </Box>

            <TableContainer component={Paper} style={{ background: isDark ? 'rgba(255,255,255,0.08)' : undefined, minHeight: 420, maxHeight: 600, overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={HEADER_STYLE}>{sortLabel('technology', 'Technology')}</TableCell>
                    <TableCell style={HEADER_STYLE}>{sortLabel('area', 'Area')}</TableCell>
                    <TableCell style={HEADER_STYLE}>{sortLabel('category', 'Category')}</TableCell>
                    <TableCell style={HEADER_STYLE}>{sortLabel('stage', 'Stage')}</TableCell>
                    <TableCell style={HEADER_STYLE}>{sortLabel('open_source_commercial', 'License')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayItems.map(item => {
                    const slug = toSlug(item.technology);
                    const s = blipStats[slug];
                    const statsLabel = s && (s.up > 0 || s.down > 0 || s.commentCount > 0)
                      ? `👍 ${s.up}  👎 ${s.down}  💬 ${s.commentCount}`
                      : 'No votes or comments yet';
                    return (
                    <Tooltip key={item.id ?? item.technology} title={statsLabel} placement="left" arrow>
                    <TableRow
                      hover
                      onClick={() => setBlipDialogItem(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell style={{ color: isDark ? '#fff' : undefined, fontWeight: 500 }}>{item.technology}</TableCell>
                      <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.6)' : undefined, fontSize: 13 }}>{item.area}</TableCell>
                      <TableCell style={{ color: isDark ? '#fff' : undefined }}>{item.category}</TableCell>
                      <TableCell>
                        {item.stage
                          ? <span style={{ color: stageColors[normalizeStageLabel(item.stage)] ?? (isDark ? '#fff' : undefined), fontWeight: 600, fontSize: 13 }}>{normalizeStageLabel(item.stage)}</span>
                          : <span style={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>—</span>}
                      </TableCell>
                      <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.65)' : undefined, fontSize: 13 }}>{item.open_source_commercial}</TableCell>
                    </TableRow>
                    </Tooltip>
                    );
                  })}
                  {displayItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', textAlign: 'center', padding: 24 }}>
                        {searchText ? 'No items match your search' : 'No items yet — click Re-seed to load from techradar_latest.md'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </div>

        {/* Tab 3: Break Down (Guide) */}
        <div style={{ display: activeTab === 2 ? undefined : 'none' }}>
            {!guideMarkdown && !guideError && (
              <Box display="flex" justifyContent="center" mt={6}>
                <CircularProgress />
              </Box>
            )}
            {guideError && (
              <Typography style={{ color: '#ef9a9a' }}>Failed to load guide.</Typography>
            )}
            {guideMarkdown && (
              <div className={classes.guideWrapper}>
                <nav className={classes.guideNav}>
                  <Typography variant="overline" style={{ padding: '8px 8px 4px', display: 'block', fontWeight: 600 }}>
                    Contents
                  </Typography>
                  <List dense disablePadding>
                    {guideToc.map(entry => (
                      <ListItem
                        key={entry.id}
                        className={`${classes.guideNavItem} ${guideActiveId === entry.id ? classes.guideNavItemActive : ''}`}
                        onClick={() => scrollToGuideSection(entry.id)}
                        style={{ paddingLeft: entry.level === 1 ? 8 : entry.level === 2 ? 20 : 32 }}
                        button
                      >
                        <ListItemText
                          className={classes.guideNavText}
                          primary={entry.text}
                        />
                      </ListItem>
                    ))}
                  </List>
                </nav>
                <div className={classes.guideContent} ref={guideContentRef}>
                  <MarkdownContent content={guideMarkdown} />
                </div>
              </div>
            )}
        </div>

        <Snackbar
          open={!!snackMsg}
          autoHideDuration={3000}
          onClose={() => setSnackMsg('')}
          message={snackMsg}
        />

        {/* ── Blip quick-view dialog (table row click) ── */}
        {blipDialogItem && (() => {
          const slug = toSlug(blipDialogItem.technology);
          const s = blipStats[slug];
          return (
            <Dialog
              open
              onClose={() => setBlipDialogItem(null)}
              maxWidth="xs"
              fullWidth
              PaperProps={{ style: { background: isDark ? '#1a1a1a' : undefined, border: isDark ? '1px solid rgba(255,255,255,0.1)' : undefined } }}
            >
              <DialogTitle disableTypography>
                <Typography variant="h6" style={{ color: isDark ? '#fff' : undefined, fontWeight: 700 }}>
                  {blipDialogItem.technology}
                </Typography>
              </DialogTitle>
              <DialogContent>
                {/* Meta */}
                <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginBottom: 16 }}>
                  {blipDialogItem.stage && (
                    <Chip label={normalizeStageLabel(blipDialogItem.stage)} size="small"
                      style={{ background: stageColors[normalizeStageLabel(blipDialogItem.stage)] ?? '#555', color: '#000', fontWeight: 600 }} />
                  )}
                  {blipDialogItem.category && (
                    <Chip label={blipDialogItem.category} size="small"
                      style={{ background: CATEGORY_COLORS[blipDialogItem.category] ?? '#555', color: '#000', fontWeight: 600 }} />
                  )}
                  {blipDialogItem.area && (
                    <Chip label={blipDialogItem.area} size="small" variant="outlined"
                      style={{ color: isDark ? 'rgba(255,255,255,0.7)' : undefined, borderColor: isDark ? 'rgba(255,255,255,0.3)' : undefined }} />
                  )}
                </Box>
                {/* Votes + comments */}
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: 20, padding: '10px 14px', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', borderRadius: 8, marginBottom: 8 }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography style={{ fontSize: 20 }}>👍</Typography>
                    <Typography variant="body1" style={{ color: isDark ? '#fff' : undefined, fontWeight: 700 }}>
                      {s?.up ?? 0}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography style={{ fontSize: 20 }}>👎</Typography>
                    <Typography variant="body1" style={{ color: isDark ? '#fff' : undefined, fontWeight: 700 }}>
                      {s?.down ?? 0}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography style={{ fontSize: 18 }}>💬</Typography>
                    <Typography variant="body1" style={{ color: isDark ? '#fff' : undefined, fontWeight: 700 }}>
                      {s?.commentCount ?? 0}
                    </Typography>
                    <Typography variant="body2" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                      {(s?.commentCount ?? 0) === 1 ? 'comment' : 'comments'}
                    </Typography>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBlipDialogItem(null)}>Close</Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => { setBlipDialogItem(null); navigateToBlip(slug); }}
                >
                  View Details
                </Button>
              </DialogActions>
            </Dialog>
          );
        })()}

        <Dialog
          open={isDownloadDialogOpen}
          onClose={() => setIsDownloadDialogOpen(false)}
          aria-labelledby="tech-radar-download-dialog-title"
        >
          <DialogTitle id="tech-radar-download-dialog-title">Download Source</DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Tech Radar content is maintained in the Git repository.
            </Typography>
            <Link href={TECH_RADAR_REPO_WEB_URL} target="_blank" rel="noopener noreferrer">
              {TECH_RADAR_REPO_WEB_URL}
            </Link>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDownloadDialogOpen(false)} color="primary">Close</Button>
            <Button
              component="a"
              href={TECH_RADAR_REPO_WEB_URL}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="contained"
            >
              Open Repository
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Content>
    </Page>
  );
};
